import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';
import authApi from '../api/authApi';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY_FIREBASE,
  authDomain: process.env.REACT_APP_BASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_BASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_BASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_BASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_BASE_APP_ID,
  measurementId: process.env.REACT_APP_BASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth();
export const db = initializeFirestore(app, {
  // Avoid unstable QUIC/WebChannel behavior on some networks/browsers.
  experimentalForceLongPolling: true,
  useFetchStreams: false,
});
export const storage = getStorage(app);

const isSupportedBrowser = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

const isDevelopMode = () => {
  return String(process.env.REACT_APP_DEVELOP).toLowerCase() === 'true';
};

export const messaging = isSupportedBrowser() && !isDevelopMode() ? getMessaging(app) : null;

const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const swConfigParams = new URLSearchParams({
        apiKey: process.env.REACT_APP_API_KEY_FIREBASE ?? '',
        authDomain: process.env.REACT_APP_BASE_AUTH_DOMAIN ?? '',
        projectId: process.env.REACT_APP_BASE_PROJECT_ID ?? '',
        storageBucket: process.env.REACT_APP_BASE_STORAGE_BUCKET ?? '',
        messagingSenderId: process.env.REACT_APP_BASE_MESSAGING_SENDER_ID ?? '',
        appId: process.env.REACT_APP_BASE_APP_ID ?? '',
      });
      const registration = await navigator.serviceWorker.register(
        `/firebase-messaging-sw.js?${swConfigParams.toString()}`,
      );
      console.log('Service Worker registered with scope:', registration?.scope);
      return registration;
    } catch (error) {
      console.error('ServiceWorker registration failed: ', error);
    }
  }
};

export const generateToken = async ({ requestPermission = false } = {}) => {
  if (!isSupportedBrowser() || isDevelopMode() || !messaging) return;
  let permission = Notification.permission;

  if (permission === 'default' && requestPermission) {
    permission = await Notification.requestPermission();
  }

  if (permission === 'granted') {
    try {
      const registration = await registerServiceWorker();
      return await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
    } catch (e) {
      console.error('FCM token generation failed: ', e);
    }
  }
};

export const ensureFirebaseSignedIn = async () => {
  if (auth.currentUser) return auth.currentUser;
  try {
    const res = await authApi.getFirebaseCustomToken();
    const token = res?.data?.data?.firebaseCustomToken;
    if (!token) return null;
    const credential = await signInWithCustomToken(auth, token);
    return credential.user;
  } catch (e) {
    console.error('ensureFirebaseSignedIn failed: ', e);
    return null;
  }
};

export const revokeToken = async () => {
  if (!isSupportedBrowser() || isDevelopMode() || !messaging) return;
  const registration = await registerServiceWorker();
  try {
    const currentToken = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!currentToken) return;
    await deleteToken(messaging);
    return currentToken;
  } catch (e) {
    console.error('FCM token revoke failed: ', e);
  }
};
